import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatPaginator,
  MatPaginatorIntl,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { map, merge, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { FoodItemModel } from 'src/app/models/food-item.model';
import { KardexMovementModel } from 'src/app/models/kardex-movement.model';
import { AyBService } from 'src/app/services/api/ayb.service';
import * as XLSX from 'xlsx';

export interface FileNumberOption {
  fileNumber: string;
  count: number;
  sampleServiceName?: string;
}

/**
 * Factory function creating a Spanish-localized `MatPaginatorIntl` configuration
 * for Kardex table pagination controls.
 *
 * @returns Configured `MatPaginatorIntl` instance with Spanish labels and range formatting.
 * @throws None.
 */
export function getSpanishKardexPaginatorIntl(): MatPaginatorIntl {
  const customPaginatorIntl = new MatPaginatorIntl();
  customPaginatorIntl.itemsPerPageLabel = 'Movimientos por página:';
  customPaginatorIntl.nextPageLabel = 'Página siguiente';
  customPaginatorIntl.previousPageLabel = 'Página anterior';
  customPaginatorIntl.firstPageLabel = 'Primera página';
  customPaginatorIntl.lastPageLabel = 'Última página';
  customPaginatorIntl.getRangeLabel = (
    pageIndex: number,
    pageSize: number,
    totalLength: number,
  ): string => {
    if (totalLength === 0 || pageSize === 0) {
      return `0 de ${totalLength}`;
    }
    const safeTotalLength = Math.max(totalLength, 0);
    const startIndex = pageIndex * pageSize;
    const endIndex =
      startIndex < safeTotalLength
        ? Math.min(startIndex + pageSize, safeTotalLength)
        : startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} de ${safeTotalLength}`;
  };
  return customPaginatorIntl;
}

@Component({
  selector: 'app-kardex',
  templateUrl: './kardex.component.html',
  styleUrls: ['./kardex.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MatPaginatorIntl, useFactory: getSpanishKardexPaginatorIntl },
  ],
})
export class KardexComponent implements OnInit, OnDestroy {
  private readonly componentDestroyed$ = new Subject<boolean>();

  /**
   * Subject used to cancel active in-flight Kardex search API calls when a new search starts
   * or when the user changes the selected SKU, avoiding redundant network queries and race conditions.
   */
  private readonly cancelActiveSearchRequests$ = new Subject<void>();

  private matPaginatorReference?: MatPaginator;
  private matSortReference?: MatSort;
  private fullFormattedKardexItemsList: KardexMovementModel[] = [];

  /**
   * Tracks the currently queried SKU code to prevent redundant consecutive searches for the same item.
   */
  private currentActiveSearchSupplyCode: string | null = null;

  /**
   * Sort direction for table records by movement date.
   * 'asc' = oldest first (chronological order)
   * 'desc' = newest first (reverse chronological order)
   */
  dateSortDirection: 'asc' | 'desc' = 'desc';

  /**
   * Setter for the MatPaginator ViewChild element.
   * Stores the paginator instance reference and updates the paginated table slice.
   *
   * @param paginatorInstance - MatPaginator instance rendered in the template.
   * @returns Void output after linking paginator reference and updating table slice.
   * @throws None.
   */
  @ViewChild(MatPaginator) set paginator(
    paginatorInstance: MatPaginator | undefined,
  ) {
    if (paginatorInstance) {
      this.matPaginatorReference = paginatorInstance;
      this.updatePaginatedTableSlice();
    }
  }

  /**
   * Getter for the active MatPaginator instance.
   *
   * @returns MatPaginator instance or undefined if not initialized.
   * @throws None.
   */
  get paginator(): MatPaginator | undefined {
    return this.matPaginatorReference;
  }

  /**
   * Setter for the MatSort ViewChild element.
   * Stores the MatSort instance reference for table sorting.
   *
   * @param sortInstance - MatSort instance rendered in the template.
   * @returns Void output after linking sort reference.
   * @throws None.
   */
  @ViewChild(MatSort) set sort(sortInstance: MatSort | undefined) {
    if (sortInstance) {
      this.matSortReference = sortInstance;
    }
  }

  /**
   * Returns the total count of Kardex movements currently filtered and available across all pages.
   *
   * @returns Numeric count of total filtered movement records.
   * @throws None.
   */
  get totalFilteredMovementsCount(): number {
    return this.fullFormattedKardexItemsList.length;
  }

  /**
   * Updates `kardexTableDataSource.data` by taking a paginated slice from `fullFormattedKardexItemsList`
   * based on current paginator pageIndex and pageSize parameters.
   *
   * @returns Void output after calculating slice range and updating table data source.
   * @throws None. Handles empty lists or missing paginator reference gracefully.
   */
  updatePaginatedTableSlice(): void {
    const totalItemsCount = this.fullFormattedKardexItemsList.length;
    if (totalItemsCount === 0) {
      this.kardexTableDataSource.data = [];
      if (this.matPaginatorReference) {
        this.matPaginatorReference.length = 0;
      }
      return;
    }

    if (!this.matPaginatorReference) {
      this.kardexTableDataSource.data = [...this.fullFormattedKardexItemsList];
      return;
    }

    this.matPaginatorReference.length = totalItemsCount;
    const activePageIndex = this.matPaginatorReference.pageIndex || 0;
    const activePageSize = this.matPaginatorReference.pageSize || 25;

    const sliceStartIndex = activePageIndex * activePageSize;
    const sliceEndIndex = Math.min(
      totalItemsCount,
      (activePageIndex + 1) * activePageSize,
    );

    this.kardexTableDataSource.data = this.fullFormattedKardexItemsList.slice(
      sliceStartIndex,
      sliceEndIndex,
    );
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Handles page navigation and page size change events emitted by MatPaginator.
   * Re-calculates the page slice for the requested page event.
   *
   * @param pageChangeEvent - PageEvent object containing pageIndex, previousPageIndex, and pageSize.
   * @returns Void output after re-slicing table data for the requested page event.
   * @throws None.
   */
  onPaginatorPageChange(pageChangeEvent: PageEvent): void {
    if (pageChangeEvent) {
      this.updatePaginatedTableSlice();
    }
  }

  /**
   * Handles sort direction changes emitted by MatSort on table column headers.
   * Re-sorts all filtered Kardex movement records by date and resets paginator to page 0.
   *
   * @param activeSortState - Sort event object containing active column name and sort direction.
   * @returns Void output after updating `dateSortDirection` and triggering table re-sort.
   * @throws None.
   */
  onSortChange(activeSortState: Sort): void {
    if (activeSortState.active === 'movementDate') {
      this.dateSortDirection =
        (activeSortState.direction as 'asc' | 'desc') || 'desc';
      this.sortAndPaginateTableData();
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Sorts the complete array of filtered Kardex movement records (`fullFormattedKardexItemsList`)
   * by movement date based on `dateSortDirection`:
   * - 'desc' (DOWN arrow ↓): Oldest to Newest (chronological order, Totals at bottom).
   * - 'asc' (UP arrow ↑): Newest to Oldest (reverse chronological order, Totals at top).
   * Resets pageIndex to 0 and updates the paginated table slice for display.
   *
   * @returns Void output after sorting array and updating paginated data source slice.
   * @throws None. Handles empty arrays gracefully.
   */
  sortAndPaginateTableData(): void {
    if (
      !this.fullFormattedKardexItemsList ||
      this.fullFormattedKardexItemsList.length === 0
    ) {
      this.updatePaginatedTableSlice();
      return;
    }

    const currentSortDirection = this.dateSortDirection;

    this.fullFormattedKardexItemsList.sort((firstRecord, secondRecord) => {
      const firstIndex = firstRecord.chronologicalOrderIndex;
      const secondIndex = secondRecord.chronologicalOrderIndex;

      // Deterministic ordinal sequence: preserves exact running balance arithmetic from Backend
      if (firstIndex !== undefined && secondIndex !== undefined) {
        return currentSortDirection === 'desc'
          ? firstIndex - secondIndex
          : secondIndex - firstIndex;
      }

      // Fallback timestamp comparison if indices are absent
      const firstTimestampMs = firstRecord.movementDate
        ? new Date(firstRecord.movementDate).getTime()
        : 0;
      const secondTimestampMs = secondRecord.movementDate
        ? new Date(secondRecord.movementDate).getTime()
        : 0;

      if (firstTimestampMs !== secondTimestampMs) {
        return currentSortDirection === 'desc'
          ? firstTimestampMs - secondTimestampMs
          : secondTimestampMs - firstTimestampMs;
      }

      const isFirstSaldoAnterior =
        firstRecord.movementTimestampId === 'SALDO_ANTERIOR';
      const isSecondSaldoAnterior =
        secondRecord.movementTimestampId === 'SALDO_ANTERIOR';

      if (isFirstSaldoAnterior && !isSecondSaldoAnterior) {
        return currentSortDirection === 'desc' ? -1 : 1;
      }
      if (!isFirstSaldoAnterior && isSecondSaldoAnterior) {
        return currentSortDirection === 'desc' ? 1 : -1;
      }

      return 0;
    });

    if (this.matPaginatorReference) {
      this.matPaginatorReference.pageIndex = 0;
    }
    this.updatePaginatedTableSlice();
  }

  isPageLoading = false;
  isInitialCatalogLoading = false;
  currentLoadingChunkBatch = 0;

  /**
   * Flag indicating whether all background movement chunks have finished loading and completion notification should be displayed.
   */
  hasFinishedLoadingAllMovements = false;

  /**
   * Timeout reference for auto-dismissing the completion banner after background loading finishes.
   */
  private completionToastTimeoutReference?: ReturnType<typeof setTimeout>;

  /**
   * Indicates whether additional movement chunks are actively being fetched in the background.
   * True when initial page loading is in progress and at least one chunk of movements has already been received.
   *
   * @returns Boolean flag indicating background streaming active status.
   * @throws None.
   */
  get isBackgroundLoadingMore(): boolean {
    return this.isPageLoading && this.kardexMovementsList.length > 0;
  }

  searchMode: 'BY_SUPPLY' | 'BY_FILE' = 'BY_SUPPLY';

  supplySearchControl = new FormControl<string | FoodItemModel>('', {
    nonNullable: true,
  });
  fileSearchControl = new FormControl<string>('', { nonNullable: true });
  periodFilterControl = new FormControl<string>('ALL', { nonNullable: true });
  movementTypeFilterControl = new FormControl<
    'ALL' | 'INGRESO' | 'EGRESO_OPERATIVO' | 'EGRESO_ADICIONAL'
  >('ALL', { nonNullable: true });
  fileNumberFilterControl = new FormControl<string>('', { nonNullable: true });
  tableTextSearchControl = new FormControl<string>('', { nonNullable: true });

  availableSuppliesList: FoodItemModel[] = [];
  filteredSuppliesAutocomplete$!: Observable<FoodItemModel[]>;

  availableFileNumbersList: FileNumberOption[] = [];
  availableMonthsList: { key: string; label: string }[] = [];
  filteredFileNumbersAutocomplete$!: Observable<FileNumberOption[]>;
  filteredHeaderFilesAutocomplete$!: Observable<FileNumberOption[]>;

  isLegendExpanded = false;

  selectedSupplyCode: string | null = null;
  selectedSupplyName: string | null = null;
  selectedSupplyUnitOfMeasure: string | null = null;

  kardexMovementsList: KardexMovementModel[] = [];
  kardexTableDataSource = new MatTableDataSource<KardexMovementModel>([]);

  // KPI Summary Metrics
  currentStockQuantityBalance = 0;
  currentWeightedAverageUnitPriceCUP = 0;
  currentTotalInventoryValuationBalance = 0;
  totalRecordedMovementsCount = 0;

  // Period Sum Totals for Table Footer
  periodInflowQuantitySum = 0;
  periodInflowCostSum = 0;
  periodOutflowQuantitySum = 0;
  periodOutflowCostSum = 0;

  kardexTableDisplayedColumns: string[] = [
    'movementDate',
    'documentType',
    'documentNumber',
    'referenceDetailInfo',
    'inflowQuantity',
    'inflowUnitPrice',
    'inflowTotalAmount',
    'outflowQuantity',
    'outflowUnitPrice',
    'outflowTotalAmount',
    'finalQuantityBalance',
    'finalWeightedAverageUnitPrice',
    'finalTotalValuationBalance',
  ];

  kardexTableTopTotalsColumns: string[] = [
    'top-total-label',
    'top-total-inflowQuantity',
    'top-total-inflowUnitPrice',
    'top-total-inflowTotalAmount',
    'top-total-outflowQuantity',
    'top-total-outflowUnitPrice',
    'top-total-outflowTotalAmount',
    'top-total-finalQuantityBalance',
    'top-total-finalWeightedAverageUnitPrice',
    'top-total-finalTotalValuationBalance',
  ];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly aybService: AyBService,
  ) {}

  ngOnInit(): void {
    this.loadInitialSuppliesCatalog();

    this.filteredSuppliesAutocomplete$ =
      this.supplySearchControl.valueChanges.pipe(
        startWith(''),
        map((inputValue) => {
          const searchInput =
            typeof inputValue === 'string'
              ? inputValue
              : inputValue?.nombreInsumo;
          return searchInput
            ? this.filterSuppliesCatalogByNameOrCode(searchInput)
            : this.availableSuppliesList.slice();
        }),
      );

    this.filteredFileNumbersAutocomplete$ =
      this.fileNumberFilterControl.valueChanges.pipe(
        startWith(''),
        map((inputValue) => this.filterFileNumbersList(inputValue)),
      );

    this.filteredHeaderFilesAutocomplete$ =
      this.fileSearchControl.valueChanges.pipe(
        startWith(''),
        map((inputValue) => this.filterFileNumbersList(inputValue)),
      );

    merge(
      this.periodFilterControl.valueChanges,
      this.movementTypeFilterControl.valueChanges,
      this.fileNumberFilterControl.valueChanges,
      this.tableTextSearchControl.valueChanges,
    )
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.applyPeriodFilterAndBuildTableData();
        this.changeDetectorRef.markForCheck();
      });

    // Check query params if navigated with supplyCode (e.g. /kardex?code=AB00001)
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((routeQueryParams) => {
        const preselectedSupplyCode =
          routeQueryParams['code'] || routeQueryParams['supplyCode'];
        if (preselectedSupplyCode) {
          this.executeKardexSearchBySupplyCode(preselectedSupplyCode);
        }
      });
  }

  ngOnDestroy(): void {
    this.stopActiveSearchRequests();

    if (this.completionToastTimeoutReference) {
      clearTimeout(this.completionToastTimeoutReference);
      this.completionToastTimeoutReference = undefined;
    }

    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  /**
   * Immediately terminates any active in-flight HTTP requests and chunk queries for Kardex movements.
   *
   * @returns Void output after emitting on the cancellation subject.
   * @throws None.
   */
  stopActiveSearchRequests(): void {
    this.cancelActiveSearchRequests$.next();
  }

  /**
   * Dismisses the background loading completion notification banner manually.
   * Clears any active auto-dismiss timeout reference.
   *
   * @returns Void output after dismissing completion notification banner.
   * @throws None.
   */
  dismissCompletionNotificationBanner(): void {
    if (this.completionToastTimeoutReference) {
      clearTimeout(this.completionToastTimeoutReference);
      this.completionToastTimeoutReference = undefined;
    }
    this.hasFinishedLoadingAllMovements = false;
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Loads the global catalog of food supplies (SKUs) from the backend API.
   * Populates `availableSuppliesList` and executes auto-search if a query parameter code is provided.
   *
   * @returns Void output after fetching supplies catalog.
   * @throws None. Handles API errors gracefully.
   */
  loadInitialSuppliesCatalog(): void {
    this.isInitialCatalogLoading = true;
    this.aybService
      .queryGetAllFoodItemsSku()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (catalogResponse) => {
          this.availableSuppliesList =
            catalogResponse?.getAllFoodItemsSku?.insumos || [];
          this.isInitialCatalogLoading = false;

          const preselectedSupplyCode =
            this.activatedRoute.snapshot.queryParams['code'] ||
            this.activatedRoute.snapshot.queryParams['supplyCode'];
          if (preselectedSupplyCode) {
            this.executeKardexSearchBySupplyCode(preselectedSupplyCode);
          }

          this.changeDetectorRef.markForCheck();
        },
        error: (loadingError) => {
          console.error(
            'Failed to load supplies catalog for Kardex:',
            loadingError,
          );
          this.isInitialCatalogLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  /**
   * Filters the available supplies catalog by matching SKU name or code with the query term.
   *
   * @param filterSearchTerm - Text query to match against supply code or name.
   * @returns Filtered array of matching `FoodItemModel` objects.
   * @throws None.
   */
  filterSuppliesCatalogByNameOrCode(filterSearchTerm: string): FoodItemModel[] {
    const lowercaseQuery = filterSearchTerm.toLowerCase();
    return this.availableSuppliesList.filter(
      (supplyItem) =>
        supplyItem.nombreInsumo?.toLowerCase().includes(lowercaseQuery) ||
        supplyItem.codigoInsumo?.toLowerCase().includes(lowercaseQuery),
    );
  }

  /**
   * Handles selection of a supply item from the primary SKU autocomplete dropdown menu.
   *
   * @param selectedSupplyItem - Selected FoodItemModel object containing supply metadata.
   * @returns Void output after setting selected supply properties and triggering search.
   * @throws None. Gracefully handles null/undefined selection values.
   */
  onSupplySelectedFromAutocomplete(selectedSupplyItem: FoodItemModel): void {
    if (selectedSupplyItem && selectedSupplyItem.codigoInsumo) {
      this.selectedSupplyCode = selectedSupplyItem.codigoInsumo;
      this.selectedSupplyName = selectedSupplyItem.nombreInsumo ?? null;
      this.selectedSupplyUnitOfMeasure = selectedSupplyItem.unidadMedida
        ? String(selectedSupplyItem.unidadMedida)
        : null;
      this.executeKardexSearchBySupplyCode(selectedSupplyItem.codigoInsumo);
    }
  }

  /**
   * Re-executes the Kardex search query for the currently selected SKU code
   * to fetch and reload the latest movement records from the backend API, forcing a clean refresh.
   *
   * @returns Void output after initiating Kardex search query.
   * @throws None. Gracefully handles scenarios where no SKU code is selected.
   */
  refreshKardexData(): void {
    if (this.selectedSupplyCode) {
      this.executeKardexSearchBySupplyCode(this.selectedSupplyCode, true);
    }
  }

  /**
   * Executes a paginated, background-chunked Kardex query for the specified SKU code.
   * Cancels any previously active in-flight chunk requests immediately to prevent redundant calls and race conditions.
   *
   * @param targetSupplyCode - SKU code of the supply to search.
   * @param forceRefresh - Optional boolean flag to bypass identical-search protection and force a fresh reload.
   * @returns Void output after starting chunked stream.
   * @throws None. Handles network failures safely without breaking component state.
   */
  executeKardexSearchBySupplyCode(
    targetSupplyCode: string,
    forceRefresh: boolean = false,
  ): void {
    if (!targetSupplyCode) return;

    let cleanSupplyCode = targetSupplyCode.trim();
    if (cleanSupplyCode.includes('-')) {
      cleanSupplyCode = cleanSupplyCode.split('-')[0].trim();
    }

    // Stop and ignore duplicate searches if the exact clean code is already loading
    if (
      !forceRefresh &&
      this.isPageLoading &&
      this.currentActiveSearchSupplyCode === cleanSupplyCode
    ) {
      return;
    }

    // 1. Cancel previous in-flight requests immediately
    this.stopActiveSearchRequests();

    this.currentActiveSearchSupplyCode = cleanSupplyCode;

    if (this.completionToastTimeoutReference) {
      clearTimeout(this.completionToastTimeoutReference);
      this.completionToastTimeoutReference = undefined;
    }
    this.hasFinishedLoadingAllMovements = false;

    this.isPageLoading = true;
    this.kardexMovementsList = [];
    this.fullFormattedKardexItemsList = [];
    this.availableFileNumbersList = [];
    this.kardexTableDataSource.data = [];
    this.currentLoadingChunkBatch = 0;

    const CHUNK_SIZE = 100;
    let currentPaginationOffset = 0;

    const fetchNextMovementChunk = () => {
      this.currentLoadingChunkBatch++;
      this.changeDetectorRef.markForCheck();

      this.aybService
        .queryGetMergeKardexBySupplyCode(
          cleanSupplyCode,
          CHUNK_SIZE,
          currentPaginationOffset,
        )
        .pipe(
          takeUntil(this.cancelActiveSearchRequests$),
          takeUntil(this.componentDestroyed$),
        )
        .subscribe({
          next: (chunkResponse) => {
            const receivedMovements =
              chunkResponse?.getMergeKardexBySupplyCode || [];
            this.kardexMovementsList = [
              ...this.kardexMovementsList,
              ...receivedMovements,
            ];

            // Assign strict backend chronological sequence index to preserve running balance calculation order
            for (let i = 0; i < this.kardexMovementsList.length; i++) {
              this.kardexMovementsList[i].chronologicalOrderIndex = i;
            }

            this.populateAvailableFileNumbersList();
            this.calculateKpiValuationSummaryMetrics();
            this.applyPeriodFilterAndBuildTableData();

            if (receivedMovements.length === CHUNK_SIZE) {
              // Fetch next chunk if more records are available
              currentPaginationOffset += CHUNK_SIZE;
              fetchNextMovementChunk();
            } else {
              // All movement chunks finished loading
              this.isPageLoading = false;
              if (this.kardexMovementsList.length > 0) {
                this.hasFinishedLoadingAllMovements = true;
                this.completionToastTimeoutReference = setTimeout(() => {
                  this.hasFinishedLoadingAllMovements = false;
                  this.changeDetectorRef.markForCheck();
                }, 6000);
              }
              this.changeDetectorRef.markForCheck();
            }
          },
          error: (chunkFetchError) => {
            console.error(
              'Failed to fetch Kardex movements chunk:',
              chunkFetchError,
            );
            this.isPageLoading = false;
            this.changeDetectorRef.markForCheck();
          },
        });
    };

    fetchNextMovementChunk();
  }

  /**
   * Filters loaded Kardex movements by itinerary file search term and displays them chronologically from oldest to newest.
   *
   * @returns Void output after updating table data source.
   * @throws None.
   */
  executeKardexSearchByItineraryFileId(): void {
    const itinerarySearchTerm = this.fileSearchControl.value
      ?.trim()
      .toLowerCase();
    if (!itinerarySearchTerm) return;

    this.fileNumberFilterControl.setValue(itinerarySearchTerm);
  }

  /**
   * Applies all table filters (Period, Movement Type, File Number Autocomplete, Text Search)
   * and constructs SUNAT-standard table data starting with SALDO ANTERIOR when applicable.
   *
   * @returns Void output after updating table data source and period total sums.
   * @throws None.
   */
  applyPeriodFilterAndBuildTableData(): void {
    if (!this.kardexMovementsList || this.kardexMovementsList.length === 0) {
      this.kardexTableDataSource.data = [];
      this.periodInflowQuantitySum = 0;
      this.periodInflowCostSum = 0;
      this.periodOutflowQuantitySum = 0;
      this.periodOutflowCostSum = 0;
      return;
    }

    const selectedFilterOption = this.periodFilterControl.value;
    const selectedMovementType = this.movementTypeFilterControl.value;
    const fileFilterTerm = this.fileNumberFilterControl.value
      .trim()
      .toLowerCase();
    const textSearchTerm = this.tableTextSearchControl.value
      .trim()
      .toLowerCase();

    let cutoffDate: Date | null = null;
    const currentNowDate = new Date();

    if (selectedFilterOption.startsWith('MONTH_')) {
      const targetYearMonth = selectedFilterOption.replace('MONTH_', '');
      cutoffDate = new Date(`${targetYearMonth}-01T00:00:00.000Z`);
    } else if (selectedFilterOption === 'LAST_7_DAYS') {
      cutoffDate = new Date(currentNowDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (selectedFilterOption === 'LAST_30_DAYS') {
      cutoffDate = new Date(
        currentNowDate.getTime() - 30 * 24 * 60 * 60 * 1000,
      );
    } else if (selectedFilterOption === 'CURRENT_MONTH') {
      cutoffDate = new Date(
        currentNowDate.getFullYear(),
        currentNowDate.getMonth(),
        1,
      );
    }

    const priorMovementsList: KardexMovementModel[] = [];
    let candidatePeriodMovements: KardexMovementModel[] = [];

    if (selectedFilterOption.startsWith('MONTH_')) {
      const targetYearMonth = selectedFilterOption.replace('MONTH_', '');
      for (const movementRecord of this.kardexMovementsList) {
        if (!movementRecord.movementDate) continue;
        const recordYearMonth = movementRecord.movementDate
          .split('T')[0]
          .substring(0, 7);
        if (recordYearMonth < targetYearMonth) {
          priorMovementsList.push(movementRecord);
        } else if (recordYearMonth === targetYearMonth) {
          candidatePeriodMovements.push(movementRecord);
        }
      }
    } else if (cutoffDate) {
      const cutoffTimeMs = cutoffDate.getTime();
      for (const movementRecord of this.kardexMovementsList) {
        const movementTimeMs = new Date(movementRecord.movementDate).getTime();
        if (movementTimeMs < cutoffTimeMs) {
          priorMovementsList.push(movementRecord);
        } else {
          candidatePeriodMovements.push(movementRecord);
        }
      }
    } else {
      candidatePeriodMovements = [...this.kardexMovementsList];
    }

    // Apply multi-field filters (Movement Type, File Number, Text Search)
    const periodMovementsList = candidatePeriodMovements.filter(
      (movementRecord) => {
        // 1. Movement Type Filter
        if (
          selectedMovementType !== 'ALL' &&
          movementRecord.movementType !== selectedMovementType
        ) {
          return false;
        }

        // 2. File Number Filter
        if (fileFilterTerm) {
          const itineraryFileNumber = (
            movementRecord.itinerarySnapshot?.fileNumber || ''
          ).toLowerCase();
          const itineraryIdString = (
            movementRecord.itineraryId || ''
          ).toLowerCase();
          if (
            !itineraryFileNumber.includes(fileFilterTerm) &&
            !itineraryIdString.includes(fileFilterTerm)
          ) {
            return false;
          }
        }

        // 3. Text Search Filter across Document Number, Type, Provider, Invoices, File, Services, Observations
        if (textSearchTerm) {
          const documentNumberString =
            this.getDocumentNumber(movementRecord).toLowerCase();
          const documentTypeCodeString =
            this.getDocumentTypeCode(movementRecord).toLowerCase();
          const providerNameString = (
            movementRecord.aybProviderSnapshot?.providerName || ''
          ).toLowerCase();
          const providerRucString = (
            movementRecord.aybProviderSnapshot?.providerRuc || ''
          ).toLowerCase();
          const invoiceNumberString = (
            movementRecord.inflowSnapshot?.invoiceNumber || ''
          ).toLowerCase();
          const receiptNumberString = (
            movementRecord.inflowSnapshot?.receiptNumber || ''
          ).toLowerCase();
          const fileNumberString = (
            movementRecord.itinerarySnapshot?.fileNumber || ''
          ).toLowerCase();
          const serviceNameString = (
            movementRecord.itineraryServiceSnapshot?.serviceName || ''
          ).toLowerCase();
          const menuNameString = (
            movementRecord.itineraryServiceSnapshot?.menuName || ''
          ).toLowerCase();
          const observationString = (
            movementRecord.observation || ''
          ).toLowerCase();

          const matchesSearchQuery =
            documentNumberString.includes(textSearchTerm) ||
            documentTypeCodeString.includes(textSearchTerm) ||
            providerNameString.includes(textSearchTerm) ||
            providerRucString.includes(textSearchTerm) ||
            invoiceNumberString.includes(textSearchTerm) ||
            receiptNumberString.includes(textSearchTerm) ||
            fileNumberString.includes(textSearchTerm) ||
            serviceNameString.includes(textSearchTerm) ||
            menuNameString.includes(textSearchTerm) ||
            observationString.includes(textSearchTerm);

          if (!matchesSearchQuery) {
            return false;
          }
        }

        return true;
      },
    );

    // Reset period sum totals
    this.periodInflowQuantitySum = 0;
    this.periodInflowCostSum = 0;
    this.periodOutflowQuantitySum = 0;
    this.periodOutflowCostSum = 0;

    for (const periodMovementRecord of periodMovementsList) {
      if (this.isInflowMovement(periodMovementRecord)) {
        this.periodInflowQuantitySum +=
          periodMovementRecord.movementQuantityDelta;
        this.periodInflowCostSum +=
          (periodMovementRecord.movementQuantityDelta < 0 ? -1 : 1) *
          periodMovementRecord.movementTotalValuation;
      } else if (this.isOutflowMovement(periodMovementRecord)) {
        this.periodOutflowQuantitySum += Math.abs(
          periodMovementRecord.movementQuantityDelta,
        );
        this.periodOutflowCostSum +=
          periodMovementRecord.movementTotalValuation;
      }
    }

    const formattedTableItemsList: KardexMovementModel[] = [];

    if (
      cutoffDate &&
      (priorMovementsList.length > 0 || periodMovementsList.length > 0)
    ) {
      let previousQuantityBalance = 0;
      let previousWeightedAveragePrice = 0;
      let previousTotalValuation = 0;
      let priorDateIsoString = cutoffDate.toISOString();

      if (priorMovementsList.length > 0) {
        const lastPriorRecord =
          priorMovementsList[priorMovementsList.length - 1];
        previousQuantityBalance = lastPriorRecord.finalQuantityBalance;
        previousWeightedAveragePrice =
          lastPriorRecord.finalWeightedAverageUnitPrice;
        previousTotalValuation = lastPriorRecord.finalTotalValuationBalance;
        priorDateIsoString = lastPriorRecord.movementDate;
      } else if (periodMovementsList.length > 0) {
        const firstPeriodRecord = periodMovementsList[0];
        previousQuantityBalance = firstPeriodRecord.previousQuantityBalance;
        previousWeightedAveragePrice =
          firstPeriodRecord.previousWeightedAverageUnitPrice;
        previousTotalValuation =
          firstPeriodRecord.previousTotalValuationBalance;
        priorDateIsoString = firstPeriodRecord.movementDate;
      }

      const saldoAnteriorRecord: KardexMovementModel = {
        supplyCode: this.selectedSupplyCode || '',
        movementTimestampId: 'SALDO_ANTERIOR',
        id: 'SALDO_ANTERIOR',
        supplyId: '',
        supplySnapshot: {
          supplyName: this.selectedSupplyName || null,
          unitOfMeasure: this.selectedSupplyUnitOfMeasure || null,
        },
        movementDate: priorDateIsoString,
        movementType: 'AJUSTE_INICIAL',
        sourceRecordId: 'SALDO_ANTERIOR',
        observation: 'SALDO INICIAL / ANTERIOR',
        previousQuantityBalance: 0,
        previousWeightedAverageUnitPrice: 0,
        previousTotalValuationBalance: 0,
        movementQuantityDelta: 0,
        movementUnitPrice: 0,
        movementTotalValuation: 0,
        finalQuantityBalance: previousQuantityBalance,
        finalWeightedAverageUnitPrice: previousWeightedAveragePrice,
        finalTotalValuationBalance: previousTotalValuation,
        createdAt: priorDateIsoString,
        updatedAt: priorDateIsoString,
        chronologicalOrderIndex: -1,
      };

      formattedTableItemsList.push(saldoAnteriorRecord);
    }

    formattedTableItemsList.push(...periodMovementsList);

    this.fullFormattedKardexItemsList = formattedTableItemsList;
    this.sortAndPaginateTableData();
  }

  /**
   * Calculates high-level valuation KPI summary metrics (stock balance, CUP price, and total inventory valuation)
   * from the latest recorded movement in the loaded Kardex ledger.
   *
   * @returns Void output after updating KPI state properties.
   * @throws None.
   */
  calculateKpiValuationSummaryMetrics(): void {
    this.totalRecordedMovementsCount = this.kardexMovementsList.length;

    if (this.kardexMovementsList.length > 0) {
      const latestMovementSnapshot =
        this.kardexMovementsList[this.kardexMovementsList.length - 1];
      this.currentStockQuantityBalance =
        latestMovementSnapshot.finalQuantityBalance;
      this.currentWeightedAverageUnitPriceCUP =
        latestMovementSnapshot.finalWeightedAverageUnitPrice;
      this.currentTotalInventoryValuationBalance =
        latestMovementSnapshot.finalTotalValuationBalance;

      const latestSupplyName =
        latestMovementSnapshot.supplySnapshot?.supplyName;
      if (!this.selectedSupplyName && latestSupplyName) {
        this.selectedSupplyName = latestSupplyName ?? null;
      }
      const latestUnitOfMeasure =
        latestMovementSnapshot.supplySnapshot?.unitOfMeasure;
      if (!this.selectedSupplyUnitOfMeasure && latestUnitOfMeasure) {
        this.selectedSupplyUnitOfMeasure = String(latestUnitOfMeasure);
      }
    } else {
      this.currentStockQuantityBalance = 0;
      this.currentWeightedAverageUnitPriceCUP = 0;
      this.currentTotalInventoryValuationBalance = 0;
    }
  }

  /**
   * Formats a supply option object or string into human-readable display text for the autocomplete input.
   *
   * @param supplyOption - FoodItemModel instance or plain string.
   * @returns Formatted option label string (e.g. 'VER00002 - AJO').
   * @throws None.
   */
  getSupplyOptionDisplayText(supplyOption: FoodItemModel | string): string {
    if (typeof supplyOption === 'string') return supplyOption;
    return supplyOption
      ? `${supplyOption.codigoInsumo} - ${supplyOption.nombreInsumo}`
      : '';
  }

  /**
   * Returns the CSS class identifier for a given movement type badge.
   *
   * @param movementType - Movement type string (e.g. 'INGRESO', 'EGRESO_OPERATIVO').
   * @returns CSS class name string.
   * @throws None.
   */
  getMovementTypeBadgeCssClass(movementType: string): string {
    switch (movementType) {
      case 'INGRESO':
        return 'badge-ingreso';
      case 'EGRESO_OPERATIVO':
        return 'badge-egreso-operativo';
      case 'EGRESO_ADICIONAL':
        return 'badge-egreso-adicional';
      case 'AJUSTE_INICIAL':
        return 'badge-anterior';
      default:
        return 'badge-default';
    }
  }

  /**
   * Determines whether a Kardex movement is an Inflow event (INGRESO, ANULACION_INGRESO, or AJUSTE_INICIAL).
   *
   * @param movementRecord - Movement record model.
   * @returns Boolean indicating if movement belongs under Inflows (ENTRADAS).
   * @throws None.
   */
  isInflowMovement(movementRecord: KardexMovementModel): boolean {
    return (
      movementRecord.movementType === 'INGRESO' ||
      (movementRecord.movementType === 'AJUSTE_INICIAL' &&
        movementRecord.id !== 'SALDO_ANTERIOR')
    );
  }

  /**
   * Determines whether a Kardex movement is an Outflow event (EGRESO_OPERATIVO, EGRESO_ADICIONAL, or ANULACION_EGRESO).
   *
   * @param movementRecord - Movement record model.
   * @returns Boolean indicating if movement belongs under Outflows (SALIDAS).
   * @throws None.
   */
  isOutflowMovement(movementRecord: KardexMovementModel): boolean {
    return (
      movementRecord.movementType === 'EGRESO_OPERATIVO' ||
      movementRecord.movementType === 'EGRESO_ADICIONAL' ||
      movementRecord.movementType === 'ANULACION_EGRESO'
    );
  }

  /**
   * Resolves the CSS badge styling class for a document reference (Factura, Boleta, File, Saldo Anterior, Otros).
   *
   * @param movementRecord - Movement record model.
   * @returns CSS class name string.
   * @throws None.
   */
  getDocumentBadgeCssClass(movementRecord: KardexMovementModel): string {
    if (
      movementRecord.id === 'SALDO_ANTERIOR' ||
      movementRecord.movementTimestampId === 'SALDO_ANTERIOR'
    ) {
      return 'badge-anterior';
    }
    const invoiceNumberString = movementRecord.inflowSnapshot?.invoiceNumber;
    const receiptNumberString = movementRecord.inflowSnapshot?.receiptNumber;

    if (invoiceNumberString) {
      return 'badge-factura';
    }
    if (receiptNumberString) {
      return 'badge-boleta';
    }
    if (
      movementRecord.itineraryId ||
      movementRecord.itinerarySnapshot?.fileNumber ||
      movementRecord.itineraryServiceSnapshot?.serviceName
    ) {
      return 'badge-file';
    }
    return 'badge-otros';
  }

  /**
   * Resolves the SUNAT document type code label for a Kardex movement.
   *
   * @param movementRecord - Kardex movement model.
   * @returns Document type code string (e.g., '01 (Factura)', '00 (File)').
   * @throws None.
   */
  getDocumentTypeCode(movementRecord: KardexMovementModel): string {
    if (
      movementRecord.id === 'SALDO_ANTERIOR' ||
      movementRecord.movementTimestampId === 'SALDO_ANTERIOR'
    ) {
      return '16 (S.Anterior)';
    }

    const invoiceNumberString = movementRecord.inflowSnapshot?.invoiceNumber;
    const receiptNumberString = movementRecord.inflowSnapshot?.receiptNumber;

    if (
      movementRecord.observation?.includes('Ajuste de cantidad') &&
      movementRecord.itinerarySnapshot?.fileNumber == null
    ) {
      return '00 (Otros)';
    }

    if (
      movementRecord.observation?.includes('Ajuste de cantidad') &&
      movementRecord.itinerarySnapshot?.fileNumber
    ) {
      return '00 (File)';
    }

    if (invoiceNumberString) return '01 (Factura)';
    if (
      receiptNumberString &&
      !movementRecord.itinerarySnapshot?.fileNumber &&
      !movementRecord.itineraryId
    ) {
      return '03 (Boleta)';
    }
    if (
      movementRecord.itineraryId ||
      movementRecord.itinerarySnapshot?.fileNumber ||
      movementRecord.itineraryServiceSnapshot?.serviceName
    ) {
      return '00 (File)';
    }
    return '00 (Otros)';
  }

  /**
   * Resolves the formatted document serial and number string for a Kardex movement.
   *
   * @param movementRecord - Kardex movement record.
   * @returns Formatted document reference string.
   * @throws None.
   */
  getDocumentNumber(movementRecord: KardexMovementModel): string {
    const invoiceNumberString = movementRecord.inflowSnapshot?.invoiceNumber;
    const receiptNumberString = movementRecord.inflowSnapshot?.receiptNumber;
    const fileNumberString = movementRecord.itinerarySnapshot?.fileNumber;
    const serviceNameString =
      movementRecord.itineraryServiceSnapshot?.serviceName;

    const documentReference =
      invoiceNumberString ||
      receiptNumberString ||
      fileNumberString ||
      (serviceNameString && !fileNumberString
        ? movementRecord.itineraryId
        : '') ||
      movementRecord.itineraryId ||
      '';

    return documentReference || '-';
  }

  /**
   * Returns the absolute numeric magnitude of a quantity or valuation number.
   *
   * @param numericValue - Target numeric value.
   * @returns Absolute number value.
   * @throws None.
   */
  getAbsNumber(numericValue: number | null | undefined): number {
    return Math.abs(Number(numericValue || 0));
  }

  /**
   * Formats a quantity number into accounting presentation. Negative numbers are enclosed in parentheses.
   *
   * @param quantityValue - Quantity value to format.
   * @returns Formatted string with 2 decimal places (enclosed in parentheses if negative).
   * @throws None.
   */
  formatAccountingQuantity(quantityValue: number | null | undefined): string {
    const parsedNumber = Number(quantityValue || 0);
    if (Math.abs(parsedNumber) < 0.0001) return '0.00';
    if (parsedNumber < 0) {
      return `(${Math.abs(parsedNumber).toFixed(2)})`;
    }
    return parsedNumber.toFixed(2);
  }

  /**
   * Formats a currency value into SUNAT PEN (S/) accounting presentation. Negative amounts are enclosed in parentheses.
   *
   * @param currencyValue - Currency value to format.
   * @returns Formatted currency string with 'S/' prefix (enclosed in parentheses if negative).
   * @throws None.
   */
  formatAccountingCurrency(currencyValue: number | null | undefined): string {
    const parsedNumber = Number(currencyValue || 0);
    if (Math.abs(parsedNumber) < 0.0001) return 'S/ 0.00';
    if (parsedNumber < 0) {
      return `(S/ ${Math.abs(parsedNumber).toFixed(2)})`;
    }
    return `S/ ${parsedNumber.toFixed(2)}`;
  }

  /**
   * Parses the raw ISO/date string returned directly from the backend into 'DD/MM/YYYY' presentation
   * using pure string manipulation, eliminating browser Date object timezone offsets.
   *
   * @param rawDateString - Target raw ISO or date string from backend payload.
   * @returns Formatted date string in 'DD/MM/YYYY' format or '-' if empty/invalid.
   * @throws None.
   */
  displayRawMovementDate(rawDateString: string | null | undefined): string {
    if (!rawDateString || typeof rawDateString !== 'string') return '-';
    const cleanedString = rawDateString.trim();
    if (!cleanedString) return '-';

    if (cleanedString.includes('-')) {
      const dateComponent = cleanedString.split('T')[0];
      const dateSegments = dateComponent.split('-');
      if (dateSegments.length === 3) {
        const [yearSegment, monthSegment, daySegment] = dateSegments;
        if (yearSegment.length === 4 && monthSegment && daySegment) {
          return `${daySegment.padStart(2, '0')}/${monthSegment.padStart(2, '0')}/${yearSegment}`;
        }
      }
    }

    if (cleanedString.includes('/')) {
      const dateSegments = cleanedString.split('/');
      if (dateSegments.length === 3) {
        const [daySegment, monthSegment, yearSegment] = dateSegments;
        if (daySegment && monthSegment && yearSegment) {
          return `${daySegment.padStart(2, '0')}/${monthSegment.padStart(2, '0')}/${yearSegment}`;
        }
      }
    }

    return cleanedString;
  }

  /**
   * Toggles the expanded visibility of the quick guide / legend card.
   *
   * @returns Void output after toggling `isLegendExpanded`.
   * @throws None.
   */
  toggleLegendExpansion(): void {
    this.isLegendExpanded = !this.isLegendExpanded;
  }

  /**
   * Exports all filtered Kardex movement records into a SUNAT-standard formatted Excel workbook
   * complete with metadata headers, active filter parameters, valuation KPI summaries, and auto-fitted columns.
   *
   * @returns Void output after generating Excel file and initiating browser file download.
   * @throws None. Handles empty data arrays gracefully.
   */
  exportToExcel(): void {
    if (
      !this.fullFormattedKardexItemsList ||
      this.fullFormattedKardexItemsList.length === 0
    ) {
      return;
    }

    const currentFormattedData = this.fullFormattedKardexItemsList;
    const nowIsoString = new Date().toISOString();
    const formattedNowDateString =
      this.displayRawMovementDate(nowIsoString) +
      ' ' +
      new Date().toLocaleTimeString('es-PE', {
        hour: '2-digit',
        minute: '2-digit',
      });

    // 1. Executive Metadata Header Block
    const reportRows: (string | number)[][] = [
      [
        'REPORTE DE KARDEX Y VALUACIÓN DE EXISTENCIAS (MÉTODO PROMEDIO PONDERADO - CUP)',
      ],
      [
        `CÓDIGO DE EXISTENCIA: ${this.selectedSupplyCode || '-'}`,
        `SKU: ${this.selectedSupplyName || '-'}`,
        `UNIDAD DE MEDIDA: ${this.selectedSupplyUnitOfMeasure || '-'}`,
        `FECHA DE EMISIÓN: ${formattedNowDateString}`,
      ],
      [],
      // 2. Active Filters Metadata Block
      ['PARÁMETROS DE FILTRADO Y PERÍODO SELECCIONADO:'],
      [
        `- Período Evaluado: ${this.getPeriodFilterLabel(this.periodFilterControl.value)}`,
      ],
      [
        `- Tipo de Movimiento: ${this.getMovementTypeFilterLabel(this.movementTypeFilterControl.value)}`,
      ],
      [
        `- File (Itinerario): ${this.fileNumberFilterControl.value || 'Todos los Files'}`,
      ],
      [
        `- Búsqueda Rápida: ${this.tableTextSearchControl.value ? '"' + this.tableTextSearchControl.value + '"' : 'Sin filtro de texto'}`,
      ],
      [],
      // 3. KPI Summary Metrics Block
      [
        'RESUMEN DE VALUACIÓN Y BALANCES:',
        `Stock Sistema Actual (Movimientos): ${this.currentStockQuantityBalance.toFixed(2)} ${this.selectedSupplyUnitOfMeasure || ''}`,
        `CUP Promedio Ponderado: S/ ${this.currentWeightedAverageUnitPriceCUP.toFixed(4)}`,
        `Saldo Valorizado Total: ${this.formatAccountingCurrency(this.currentTotalInventoryValuationBalance)}`,
        `Total Movimientos en Reporte: ${currentFormattedData.length}`,
      ],
      [],
      // 4. Data Table Column Headers
      [
        'FECHA',
        'TIPO',
        'SERIE-NÚMERO',
        'PROVEEDOR / FILE / CONCEPTO',
        'ENTRADAS CANTIDAD',
        'ENTRADAS COSTO UNITARIO',
        'ENTRADAS COSTO TOTAL',
        'SALIDAS CANTIDAD',
        'SALIDAS COSTO UNITARIO',
        'SALIDAS COSTO TOTAL',
        'SALDO FINAL CANTIDAD',
        'SALDO FINAL COSTO UNITARIO (CUP)',
        'SALDO FINAL COSTO TOTAL',
      ],
    ];

    // 5. Data Table Rows
    for (const movementRecord of currentFormattedData) {
      const isIngresoMovement = this.isInflowMovement(movementRecord);
      const isEgresoMovement = this.isOutflowMovement(movementRecord);

      const conceptDetailString =
        movementRecord.aybProviderSnapshot?.providerName ||
        (movementRecord.itinerarySnapshot?.fileNumber
          ? `File: ${movementRecord.itinerarySnapshot.fileNumber}${
              movementRecord.itineraryServiceSnapshot?.serviceName
                ? ' (' +
                  movementRecord.itineraryServiceSnapshot.serviceName +
                  (movementRecord.itineraryServiceSnapshot?.menuName
                    ? ' - ' + movementRecord.itineraryServiceSnapshot.menuName
                    : '') +
                  ')'
                : ''
            }`
          : movementRecord.observation || '-');

      reportRows.push([
        this.displayRawMovementDate(movementRecord.movementDate),
        this.getDocumentTypeCode(movementRecord),
        this.getDocumentNumber(movementRecord),
        conceptDetailString,
        isIngresoMovement ? movementRecord.movementQuantityDelta : 0,
        isIngresoMovement ? movementRecord.movementUnitPrice : 0,
        isIngresoMovement
          ? (movementRecord.movementQuantityDelta < 0 ? -1 : 1) *
            movementRecord.movementTotalValuation
          : 0,
        isEgresoMovement ? Math.abs(movementRecord.movementQuantityDelta) : 0,
        isEgresoMovement ? movementRecord.movementUnitPrice : 0,
        isEgresoMovement ? movementRecord.movementTotalValuation : 0,
        movementRecord.finalQuantityBalance,
        movementRecord.finalWeightedAverageUnitPrice,
        movementRecord.finalTotalValuationBalance,
      ]);
    }

    // 6. Totals Footer Row
    const latestMovementSnapshot =
      currentFormattedData[currentFormattedData.length - 1];
    reportRows.push([]);
    reportRows.push([
      'TOTAL x ARTÍCULO:',
      '',
      '',
      'SUMATORIA TOTAL DEL PERÍODO EVALUADO',
      this.periodInflowQuantitySum,
      '',
      this.periodInflowCostSum,
      this.periodOutflowQuantitySum,
      '',
      this.periodOutflowCostSum,
      latestMovementSnapshot ? latestMovementSnapshot.finalQuantityBalance : 0,
      latestMovementSnapshot
        ? latestMovementSnapshot.finalWeightedAverageUnitPrice
        : 0,
      latestMovementSnapshot
        ? latestMovementSnapshot.finalTotalValuationBalance
        : 0,
    ]);

    const excelWorksheet = XLSX.utils.aoa_to_sheet(reportRows);

    excelWorksheet['!cols'] = [
      { wch: 14 },
      { wch: 16 },
      { wch: 18 },
      { wch: 55 },
      { wch: 18 },
      { wch: 24 },
      { wch: 22 },
      { wch: 18 },
      { wch: 24 },
      { wch: 22 },
      { wch: 20 },
      { wch: 32 },
      { wch: 26 },
    ];

    const excelWorkbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      excelWorkbook,
      excelWorksheet,
      `Kardex_${this.selectedSupplyCode || 'Reporte'}`,
    );

    const timeStampString = new Date().toISOString().replace(/[:.]/g, '-');
    const generatedFileName = `Kardex_${this.selectedSupplyCode || 'Articulo'}_${timeStampString}.xlsx`;
    XLSX.writeFile(excelWorkbook, generatedFileName);
  }

  /**
   * Summarizes available file numbers and sample service names from Kardex movement records
   * to populate the file numbers filter dropdown.
   *
   * @returns Void output after updating `availableFileNumbersList`.
   * @throws None.
   */
  populateAvailableFileNumbersList(): void {
    const fileNumberMap = new Map<
      string,
      { count: number; sampleServiceName?: string }
    >();

    for (const movementRecord of this.kardexMovementsList) {
      const itineraryFileNumber = movementRecord.itinerarySnapshot?.fileNumber;
      if (itineraryFileNumber) {
        const serviceName =
          movementRecord.itineraryServiceSnapshot?.serviceName;
        const existingEntry = fileNumberMap.get(itineraryFileNumber);
        if (existingEntry) {
          existingEntry.count++;
          if (!existingEntry.sampleServiceName && serviceName) {
            existingEntry.sampleServiceName = serviceName;
          }
        } else {
          fileNumberMap.set(itineraryFileNumber, {
            count: 1,
            sampleServiceName: serviceName ?? undefined,
          });
        }
      }
    }

    this.availableFileNumbersList = Array.from(fileNumberMap.entries())
      .map(([fileNumberString, detailEntry]) => ({
        fileNumber: fileNumberString,
        count: detailEntry.count,
        sampleServiceName: detailEntry.sampleServiceName,
      }))
      .sort((firstFileOption, secondFileOption) =>
        firstFileOption.fileNumber.localeCompare(secondFileOption.fileNumber),
      );

    this.populateAvailableMonthsList();
  }

  /**
   * Filters the file numbers autocomplete list based on the search query.
   *
   * @param filterSearchTerm - Text query to match against file numbers or service names.
   * @returns Filtered array of `FileNumberOption` objects.
   * @throws None.
   */
  filterFileNumbersList(filterSearchTerm: string): FileNumberOption[] {
    if (!filterSearchTerm) return this.availableFileNumbersList;
    const cleanSearchQuery = filterSearchTerm.toLowerCase().trim();
    return this.availableFileNumbersList.filter(
      (fileOption) =>
        fileOption.fileNumber.toLowerCase().includes(cleanSearchQuery) ||
        fileOption.sampleServiceName?.toLowerCase().includes(cleanSearchQuery),
    );
  }

  /**
   * Returns the count of currently active search and parameter filters.
   *
   * @returns Integer count of active filter controls.
   * @throws None.
   */
  getActiveFiltersCount(): number {
    let activeCount = 0;
    if (this.periodFilterControl.value !== 'ALL') activeCount++;
    if (this.movementTypeFilterControl.value !== 'ALL') activeCount++;
    if (this.fileNumberFilterControl.value.trim() !== '') activeCount++;
    if (this.tableTextSearchControl.value.trim() !== '') activeCount++;
    return activeCount;
  }

  /**
   * Resets all period, movement type, file number, and quick search filters back to their default values.
   *
   * @returns Void output after resetting filter controls.
   * @throws None.
   */
  resetAllFilters(): void {
    this.periodFilterControl.setValue('ALL', { emitEvent: false });
    this.movementTypeFilterControl.setValue('ALL', { emitEvent: false });
    this.fileNumberFilterControl.setValue('', { emitEvent: false });
    this.tableTextSearchControl.setValue('', { emitEvent: true });
  }

  /**
   * Extracts distinct year-month dates from loaded movements to populate the month dropdown list.
   *
   * @returns Void output after building `availableMonthsList`.
   * @throws None.
   */
  populateAvailableMonthsList(): void {
    const yearMonthSet = new Set<string>();
    for (const movementRecord of this.kardexMovementsList) {
      if (
        movementRecord.movementDate &&
        typeof movementRecord.movementDate === 'string'
      ) {
        const datePartString = movementRecord.movementDate.split('T')[0];
        const dateParts = datePartString.split('-');
        if (dateParts.length >= 2) {
          yearMonthSet.add(`${dateParts[0]}-${dateParts[1]}`);
        }
      }
    }

    const monthNamesSpanish = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];

    this.availableMonthsList = Array.from(yearMonthSet)
      .sort((firstMonthKey, secondMonthKey) =>
        secondMonthKey.localeCompare(firstMonthKey),
      )
      .map((yearMonthKey) => {
        const [yearString, monthString] = yearMonthKey.split('-');
        const monthIndex = parseInt(monthString, 10) - 1;
        const monthDisplayName = monthNamesSpanish[monthIndex] || monthString;
        return {
          key: yearMonthKey,
          label: `${monthDisplayName} ${yearString}`,
        };
      });
  }

  /**
   * Resolves the human-readable display label for a period filter value.
   *
   * @param periodFilterValue - Period filter identifier.
   * @returns Human-readable Spanish label string.
   * @throws None.
   */
  getPeriodFilterLabel(periodFilterValue: string): string {
    if (periodFilterValue.startsWith('MONTH_')) {
      const monthKey = periodFilterValue.replace('MONTH_', '');
      const foundMonthEntry = this.availableMonthsList.find(
        (monthEntry) => monthEntry.key === monthKey,
      );
      return foundMonthEntry ? foundMonthEntry.label : monthKey;
    }
    switch (periodFilterValue) {
      case 'LAST_7_DAYS':
        return 'Últimos 7 días';
      case 'LAST_30_DAYS':
        return 'Últimos 30 días';
      case 'CURRENT_MONTH':
        return 'Mes Actual';
      default:
        return 'Todos los Movimientos';
    }
  }

  /**
   * Resolves the human-readable display label for a movement type filter value.
   *
   * @param movementTypeValue - Movement type identifier.
   * @returns Human-readable Spanish label string.
   * @throws None.
   */
  getMovementTypeFilterLabel(movementTypeValue: string): string {
    switch (movementTypeValue) {
      case 'INGRESO':
        return 'Ingresos (Compras)';
      case 'EGRESO_OPERATIVO':
        return 'Egresos Operativos (Files)';
      case 'EGRESO_ADICIONAL':
        return 'Egresos Adicionales';
      default:
        return 'Todos los Tipos';
    }
  }
}
